import { useEffect, useRef, useState } from "react";
import { Camera, RotateCcw, Check } from "lucide-react";

export const CameraCapture = ({ onCapture, onCancel }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [error, setError] = useState(null);
  const [captured, setCaptured] = useState(null);
  const [facing, setFacing] = useState("user");

  useEffect(() => {
    let s;
    (async () => {
      try {
        s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: facing, width: 1280, height: 1280 },
          audio: false,
        });
        if (videoRef.current) videoRef.current.srcObject = s;
        setStream(s);
      } catch (e) {
        setError("Camera access denied. Please allow camera in your browser.");
      }
    })();
    return () => {
      s?.getTracks().forEach((t) => t.stop());
    };
  }, [facing]);

  const snap = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return;
    const size = Math.min(v.videoWidth, v.videoHeight);
    c.width = size;
    c.height = size;
    const ctx = c.getContext("2d");
    // crop to square center
    const sx = (v.videoWidth - size) / 2;
    const sy = (v.videoHeight - size) / 2;
    ctx.drawImage(v, sx, sy, size, size, 0, 0, size, size);
    setCaptured(c.toDataURL("image/jpeg", 0.9));
  };

  const confirm = () => {
    if (captured) {
      stream?.getTracks().forEach((t) => t.stop());
      onCapture(captured);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0F0E13] flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <span className="text-xs uppercase tracking-[0.4em] text-[#E3C2B0]">Live Capture</span>
          <h2 className="font-serif text-3xl mt-2 font-light">Look straight ahead</h2>
        </div>

        <div className="relative aspect-square bg-black rounded-sm overflow-hidden border border-white/10">
          {!captured && (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="absolute inset-0 w-full h-full object-cover"
              style={{ transform: facing === "user" ? "scaleX(-1)" : "none" }}
            />
          )}
          {captured && <img src={captured} alt="captured" className="absolute inset-0 w-full h-full object-cover" />}
          <canvas ref={canvasRef} className="hidden" />
          {error && (
            <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-red-300 text-sm">
              {error}
            </div>
          )}
        </div>

        <div className="mt-8 flex items-center justify-center gap-4">
          {!captured ? (
            <>
              <button
                onClick={onCancel}
                data-testid="camera-cancel"
                className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] hover:text-[#F7F5F2] px-6 py-3"
              >
                Cancel
              </button>
              <button
                onClick={snap}
                disabled={!!error}
                data-testid="camera-snap"
                className="w-16 h-16 rounded-full bg-[#E3C2B0] flex items-center justify-center hover:bg-[#F0D4C5] disabled:opacity-30"
              >
                <Camera className="w-6 h-6 text-[#0A0908]" strokeWidth={1.5} />
              </button>
              <button
                onClick={() => setFacing(facing === "user" ? "environment" : "user")}
                data-testid="camera-flip"
                className="text-xs uppercase tracking-[0.3em] text-[#A8A6A1] hover:text-[#F7F5F2] px-6 py-3"
              >
                Flip
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setCaptured(null)}
                data-testid="camera-retake"
                className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#A8A6A1] hover:text-[#F7F5F2] px-6 py-3"
              >
                <RotateCcw className="w-4 h-4" strokeWidth={1.5} /> Retake
              </button>
              <button
                onClick={confirm}
                data-testid="camera-confirm"
                className="inline-flex items-center gap-2 bg-[#E3C2B0] text-[#0A0908] px-8 py-3 rounded-sm hover:bg-[#F0D4C5] text-xs uppercase tracking-[0.3em]"
              >
                <Check className="w-4 h-4" strokeWidth={1.5} /> Use Photo
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
